import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../Card';
import { Button } from '../Button';
import { StatusBadge } from './StatusBadge';
import { varinymAPI } from '../../lib/varinym-api';
import type { PaddleTier, PaddleBillingCycle, PaddleCheckoutResponse, UpgradeResponse } from '../../types';

interface PlanComparisonCardProps {
  currentTier: PaddleTier;
  currentCycle: PaddleBillingCycle;
  selectedCycle: PaddleBillingCycle;
  onCycleChange: (cycle: PaddleBillingCycle) => void;
  userEmail?: string;
  onUpgradeSuccess?: () => void;
}

export function PlanComparisonCard({
  currentTier,
  currentCycle,
  selectedCycle,
  onCycleChange,
  userEmail,
  onUpgradeSuccess,
}: PlanComparisonCardProps) {
  const queryClient = useQueryClient();

  // Fetch available plans
  const { data: plansData, isLoading } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => varinymAPI.getPlans(),
  });

  // Upgrade mutation
  const upgradeMutation = useMutation<
    PaddleCheckoutResponse | UpgradeResponse,
    Error,
    { tier: PaddleTier; cycle: PaddleBillingCycle }
  >({
    mutationFn: ({ tier, cycle }) => {
      const isDeveloperUpgrade = currentTier === 'developer';

      // Free -> Paid: Use checkout flow
      if (isDeveloperUpgrade) {
        return varinymAPI.createCheckoutSession(tier, cycle, userEmail || '');
      }

      // Paid -> Paid: Use upgrade endpoint
      return varinymAPI.upgradePlan(tier, cycle);
    },
    onSuccess: (response) => {
      // If response has checkout_url, redirect to checkout
      if ('checkout_url' in response && response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }

      // Otherwise, it's an immediate upgrade
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });

      if (onUpgradeSuccess) {
        onUpgradeSuccess();
      }

      alert('Your plan has been updated successfully!');
    },
    onError: (err: Error) => {
      alert(`Failed to upgrade plan: ${err.message}`);
    },
  });

  const handleSelectPlan = (tier: PaddleTier) => {
    if (tier === currentTier && selectedCycle === currentCycle) {
      return; // Already on this plan
    }

    const planName = tier.charAt(0).toUpperCase() + tier.slice(1);
    const cycleText = selectedCycle === 'monthly' ? 'Monthly' : 'Annual';

    const confirmMessage = currentTier === 'developer'
      ? `Upgrade to ${planName} (${cycleText} billing)?`
      : `Switch to ${planName} (${cycleText} billing)? You will be charged a prorated amount.`;

    if (confirm(confirmMessage)) {
      upgradeMutation.mutate({ tier, cycle: selectedCycle });
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getTierOrder = (tier: PaddleTier): number => {
    const order: Record<PaddleTier, number> = {
      developer: 0,
      starter: 1,
      growth: 2,
      professional: 3,
      enterprise: 4,
    };
    return order[tier];
  };

  if (isLoading) {
    return (
      <Card title="Available Plans">
        <div className="text-center py-8 text-muted-foreground">Loading plans...</div>
      </Card>
    );
  }

  if (!plansData?.plans || plansData.plans.length === 0) {
    return (
      <Card title="Available Plans">
        <div className="text-center py-8 text-muted-foreground">
          No plans available at the moment.
        </div>
      </Card>
    );
  }

  // Sort plans by tier order
  const sortedPlans = [...plansData.plans].sort((a, b) => getTierOrder(a.tier) - getTierOrder(b.tier));

  // Filter out developer tier from upgrade options if user is already on paid tier
  const displayPlans = currentTier === 'developer'
    ? sortedPlans
    : sortedPlans.filter(p => p.tier !== 'developer');

  return (
    <Card title="Available Plans & Pricing">
      <div className="space-y-6">
        {/* Billing Cycle Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex rounded-lg border border-border p-1 bg-muted">
            <button
              onClick={() => onCycleChange('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedCycle === 'monthly'
                  ? 'bg-background text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => onCycleChange('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedCycle === 'annual'
                  ? 'bg-background text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className="ml-1 text-xs text-success">(Save 20%)</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayPlans.map((plan) => {
            const price = selectedCycle === 'monthly' ? plan.pricing.monthly : plan.pricing.annual;
            const isCurrentPlan = plan.tier === currentTier && selectedCycle === currentCycle;
            const isCurrentTier = plan.tier === currentTier;
            const priceIsNumber = typeof price === 'number';

            return (
              <div
                key={plan.tier}
                className={`relative rounded-lg border-2 p-6 transition-all ${
                  isCurrentPlan
                    ? 'border-primary bg-primary/5 shadow-md'
                    : plan.recommended
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-background hover:border-primary/50'
                }`}
              >
                {/* Recommended Badge */}
                {plan.recommended && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      Recommended
                    </span>
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      Current Plan
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-4">
                  <StatusBadge type="tier" value={plan.tier} className="mb-2" />
                  <h3 className="text-xl font-bold text-foreground mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{plan.who_for}</p>

                  <div className="mb-2">
                    {priceIsNumber ? (
                      <>
                        <span className="text-3xl font-bold text-foreground">
                          {price === 0 ? 'Free' : formatPrice(price as number, 'USD')}
                        </span>
                        {price > 0 && (
                          <span className="text-sm text-muted-foreground">
                            /{selectedCycle === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-2xl font-bold text-foreground capitalize">{price}</span>
                    )}
                  </div>

                  {plan.pricing.description && (
                    <p className="text-xs text-muted-foreground mb-1">{plan.pricing.description}</p>
                  )}

                  {selectedCycle === 'annual' && priceIsNumber && price > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {formatPrice((price as number) / 12, 'USD')}/mo billed annually
                    </p>
                  )}

                  {plan.pricing.annual_discount && selectedCycle === 'annual' && (
                    <p className="text-xs text-success mt-1">Save {plan.pricing.annual_discount}</p>
                  )}
                </div>

                {/* Plan Limits */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Protected Identities</span>
                    <span className="font-medium text-foreground">
                      {typeof plan.limits.identities === 'number'
                        ? plan.limits.identities.toLocaleString()
                        : plan.limits.identities}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Custom Contexts</span>
                    <span className="font-medium text-foreground capitalize">
                      {plan.limits.contexts_max}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-muted-foreground">Max Aliases</span>
                    <span className="font-medium text-foreground">
                      {typeof plan.limits.aliases_max === 'number'
                        ? plan.limits.aliases_max.toLocaleString()
                        : plan.limits.aliases_max}
                    </span>
                  </div>
                </div>

                {/* Key Features */}
                <div className="space-y-1.5 mb-6 text-xs">
                  {plan.tier === 'developer' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-success">✓</span>
                      <span className="text-foreground">Evaluate all base paid plan features</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-success">✓</span>
                        <span className="text-foreground capitalize">
                          {plan.features.bulk_operations} Bulk Operations
                        </span>
                      </div>
                      {plan.features.masked_pii && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">Masked PII</span>
                        </div>
                      )}
                      {(plan.features.webhooks === true || plan.features.webhooks === 'advanced') && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">
                            {plan.features.webhooks === 'advanced' ? 'Advanced Webhooks' : 'Webhooks'}
                          </span>
                        </div>
                      )}
                      {plan.features.custom_domains && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">
                            {plan.features.custom_domains === 'unlimited' ? 'Unlimited Custom Domains' : 'Custom Domains'}
                          </span>
                        </div>
                      )}
                      {plan.features.audit_logs && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">Audit Logs</span>
                        </div>
                      )}
                      {plan.features.compliance_reports && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">Compliance Reports</span>
                        </div>
                      )}
                      {plan.features.sso && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">SSO</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-success">✓</span>
                        <span className="text-foreground capitalize">{plan.features.support} Support</span>
                      </div>
                      {plan.features.sla && (
                        <div className="flex items-center gap-2">
                          <span className="text-success">✓</span>
                          <span className="text-foreground">SLA: {plan.features.sla}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Action Button */}
                {plan.contact_sales ? (
                  <Button
                    onClick={() => window.location.href = 'mailto:sales@varinym.com?subject=Enterprise Plan Inquiry'}
                    variant="primary"
                    className="w-full"
                  >
                    Contact Sales
                  </Button>
                ) : getTierOrder(plan.tier) < getTierOrder(currentTier) && !isCurrentTier ? (
                  <p className="text-sm text-center text-muted-foreground">
                    To downgrade to this plan, please contact{' '}
                    <a href="mailto:support@varinym.com" className="text-primary hover:underline">
                      support@varinym.com
                    </a>
                  </p>
                ) : (
                  <Button
                    onClick={() => handleSelectPlan(plan.tier)}
                    disabled={isCurrentPlan || upgradeMutation.isPending}
                    isLoading={upgradeMutation.isPending}
                    variant={plan.recommended && !isCurrentTier ? 'primary' : 'secondary'}
                    className="w-full"
                  >
                    {isCurrentPlan
                      ? 'Current Plan'
                      : isCurrentTier
                      ? 'Change Billing Cycle'
                      : 'Upgrade'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          {currentTier !== 'developer' && (
            <p className="text-xs text-muted-foreground mt-2">
              Upgrades are prorated. Downgrades take effect at the end of your billing period.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
